import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, ArrowRight, ImageIcon, FileText, MapPin, Loader2, CheckCircle2, Clock } from "lucide-react";
import { PageHeader } from "../components/Brand";
import { useApp, API } from "../AppContext";

const STATUS_BADGE = {
  analyzed: { fr: "Analysée", en: "Analyzed", color: "rgba(212,175,55,0.85)", bg: "rgba(212,175,55,0.10)" },
  estimated: { fr: "Estimée", en: "Estimated", color: "#4ADE80", bg: "rgba(74,222,128,0.10)" },
  quote_created: { fr: "Devis créé", en: "Quote created", color: "#4ADE80", bg: "rgba(74,222,128,0.15)" },
};

const STYLE_LABEL = { modern: "Moderne", classic: "Classique", luxury: "Luxe", rustic: "Rustique", industrial: "Industriel", minimalist: "Minimaliste" };

export default function VisionHistory() {
  const { lang, token } = useApp();
  const isFr = lang === "fr";
  const nav = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [compareOpen, setCompareOpen] = useState(null); // session_id
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/vision/history`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSessions(data.sessions || []);
      } catch (e) {
        setError(e?.response?.data?.detail || (isFr ? "Impossible de charger l'historique" : "Cannot load history"));
      } finally { setLoading(false); }
    })();
  }, [token, isFr]);

  const openCompare = async (sessionId) => {
    setCompareOpen(sessionId);
    setCompareLoading(true);
    setCompareData(null);
    try {
      const { data } = await axios.get(`${API}/vision/compare-region/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompareData(data);
    } catch (e) {
      setCompareData({ error: e?.response?.data?.detail || (isFr ? "Comparaison indisponible" : "Compare failed") });
    } finally { setCompareLoading(false); }
  };

  const fmt = (n) => new Intl.NumberFormat(isFr ? "fr-CA" : "en-CA", { style: "currency", currency: "CAD" }).format(n || 0);

  return (
    <div data-testid="vision-history-page">
      <PageHeader
        title={isFr ? "Historique Vision IA" : "Vision AI History"}
        action={
          <button onClick={() => nav("/app/quotes/new/vision")} data-testid="new-vision-btn"
            className="text-xs font-bold px-4 py-2 rounded-full inline-flex items-center gap-2"
            style={{ background: "linear-gradient(180deg, #F5D062 0%, #D4AF37 100%)", color: "#0B0B0B" }}>
            <Sparkles size={14} /> {isFr ? "Nouvelle analyse" : "New analysis"}
          </button>
        }
      />

      {loading && (
        <div className="text-center py-24" data-testid="history-loading">
          <Loader2 size={32} className="animate-spin mx-auto mb-3" style={{ color: "var(--pg-gold)" }} />
          <div className="text-sm" style={{ color: "var(--pg-text-secondary)" }}>{isFr ? "Chargement…" : "Loading…"}</div>
        </div>
      )}

      {error && (
        <div className="rounded-xl p-4" data-testid="history-error"
          style={{ background: "rgba(192,0,0,0.10)", border: "1px solid rgba(192,0,0,0.35)", color: "var(--pg-red)" }}>
          {error}
        </div>
      )}

      {!loading && !error && sessions.length === 0 && (
        <div className="rounded-2xl p-12 text-center" data-testid="history-empty"
          style={{ background: "var(--pg-bg-card)", border: "1px dashed var(--pg-border)" }}>
          <Sparkles size={40} style={{ color: "var(--pg-gold)" }} className="mx-auto mb-4" />
          <h3 className="text-lg font-bold mb-2" style={{ color: "var(--pg-text-primary)" }}>
            {isFr ? "Aucune analyse Vision" : "No Vision analyses yet"}
          </h3>
          <p className="text-sm mb-6" style={{ color: "var(--pg-text-muted)" }}>
            {isFr ? "Prenez une photo d'une pièce ou de votre façade — l'IA génère 3 rendus et l'estimation complète."
                  : "Take a photo of a room or exterior — the AI generates 3 renders and a full estimate."}
          </p>
          <Link to="/app/quotes/new/vision" data-testid="empty-cta"
            className="inline-flex items-center gap-2 text-xs font-bold px-5 py-2.5 rounded-full"
            style={{ background: "linear-gradient(180deg, #F5D062 0%, #D4AF37 100%)", color: "#0B0B0B" }}>
            <Sparkles size={14} /> {isFr ? "Créer ma première analyse" : "Create my first analysis"}
          </Link>
        </div>
      )}

      {!loading && sessions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sessions.map((s) => {
            const badge = STATUS_BADGE[s.quote_id ? "quote_created" : s.status] || STATUS_BADGE.analyzed;
            return (
              <div key={s.id} data-testid={`history-session-${s.id}`}
                className="rounded-2xl overflow-hidden flex flex-col"
                style={{ background: "var(--pg-bg-card)", border: "1px solid var(--pg-border)" }}>
                <div className="aspect-video relative" style={{ background: "var(--pg-bg-tertiary)" }}>
                  {s.thumb_b64 ? (
                    <img src={`data:image/png;base64,${s.thumb_b64}`} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon size={32} style={{ color: "var(--pg-text-muted)" }} />
                    </div>
                  )}
                  <div className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold"
                    style={{ background: badge.bg, color: badge.color, border: `1px solid ${badge.color}55` }}>
                    {s.quote_id ? <CheckCircle2 size={10} /> : <Clock size={10} />}
                    <span>{isFr ? badge.fr : badge.en}</span>
                  </div>
                  {s.photo_count > 1 && (
                    <div className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold"
                      style={{ background: "rgba(11,12,15,0.75)", color: "#F5F5F5" }}>
                      <ImageIcon size={9} /> ×{s.photo_count}
                    </div>
                  )}
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <div className="text-[10px] uppercase tracking-widest font-semibold mb-1" style={{ color: "var(--pg-gold)" }}>
                    {s.chosen_style ? STYLE_LABEL[s.chosen_style] || s.chosen_style : (isFr ? "Non estimée" : "Not estimated")}
                    {s.region_label && <span className="ml-2 opacity-70">· {s.region_label}</span>}
                  </div>
                  <div className="text-sm font-bold line-clamp-2 mb-1" style={{ color: "var(--pg-text-primary)" }}>
                    {s.title || (s.custom_description || s.room_type)}
                  </div>
                  {s.custom_description && s.title && (
                    <div className="text-[11px] line-clamp-2 mb-2" style={{ color: "var(--pg-text-muted)" }}>
                      « {s.custom_description} »
                    </div>
                  )}
                  <div className="mt-auto flex items-end justify-between gap-2 pt-3">
                    <div>
                      <div className="text-[10px] uppercase font-semibold" style={{ color: "var(--pg-text-muted)" }}>
                        {isFr ? "Total estimé" : "Estimated total"}
                      </div>
                      <div className="text-lg font-black" style={{ color: "var(--pg-gold)" }}>
                        {s.grand_total ? fmt(s.grand_total) : "—"}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      {s.grand_total && (
                        <button onClick={() => openCompare(s.id)} data-testid={`compare-btn-${s.id}`}
                          className="text-[10px] font-bold px-2.5 py-1.5 rounded-full inline-flex items-center gap-1"
                          style={{ background: "rgba(212,175,55,0.10)", color: "var(--pg-gold)", border: "1px solid rgba(212,175,55,0.35)" }}>
                          <MapPin size={10} /> {isFr ? "Comparer régions" : "Compare regions"}
                        </button>
                      )}
                      {s.quote_id && (
                        <Link to={`/app/quotes/${s.quote_id}`} data-testid={`open-quote-${s.id}`}
                          className="text-[10px] font-bold px-2.5 py-1.5 rounded-full inline-flex items-center gap-1"
                          style={{ background: "var(--pg-gold)", color: "var(--pg-text-inverse)" }}>
                          <FileText size={10} /> {isFr ? "Devis" : "Quote"} <ArrowRight size={10} />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Region comparator modal */}
      {compareOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.75)" }}
          onClick={() => { setCompareOpen(null); setCompareData(null); }}
          data-testid="compare-modal">
          <div onClick={(e) => e.stopPropagation()}
            className="rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
            style={{ background: "var(--pg-bg-card)", border: "1px solid var(--pg-border)" }}>
            <div className="p-6">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: "var(--pg-gold)" }}>
                    <MapPin size={11} className="inline mr-1" />
                    {isFr ? "Comparateur régional Québec" : "Quebec regional comparator"}
                  </div>
                  <h3 className="text-lg font-bold mt-1" style={{ color: "var(--pg-text-primary)" }}>
                    {isFr ? "Impact du multiplicateur régional sur la main-d'œuvre"
                          : "Regional labor multiplier impact"}
                  </h3>
                </div>
                <button onClick={() => { setCompareOpen(null); setCompareData(null); }} data-testid="compare-close"
                  className="text-xs font-bold px-3 py-1.5 rounded-full"
                  style={{ background: "rgba(255,255,255,0.05)", color: "var(--pg-text-secondary)" }}>
                  {isFr ? "Fermer" : "Close"}
                </button>
              </div>
              {compareLoading && (
                <div className="py-8 text-center">
                  <Loader2 size={24} className="animate-spin mx-auto mb-2" style={{ color: "var(--pg-gold)" }} />
                </div>
              )}
              {compareData?.error && (
                <div className="rounded-xl p-4 text-sm" style={{ background: "rgba(192,0,0,0.10)", color: "var(--pg-red)" }}>
                  {compareData.error}
                </div>
              )}
              {compareData?.comparisons && (
                <div className="space-y-2" data-testid="compare-list">
                  {compareData.comparisons.map((c) => (
                    <div key={c.region_code}
                      className="rounded-xl p-3 flex items-center justify-between gap-3"
                      style={{
                        background: c.is_current ? "rgba(212,175,55,0.10)" : "var(--pg-bg-tertiary)",
                        border: `1px solid ${c.is_current ? "var(--pg-gold)" : "var(--pg-border)"}`,
                      }}
                      data-testid={`compare-row-${c.region_code}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold" style={{ color: c.is_current ? "var(--pg-gold)" : "var(--pg-text-primary)" }}>
                            {isFr ? c.region_label_fr : (c.region_label_en || c.region_label_fr)}
                          </span>
                          {c.is_current && (
                            <span className="text-[9px] uppercase font-black px-1.5 py-0.5 rounded-full"
                              style={{ background: "var(--pg-gold)", color: "var(--pg-text-inverse)" }}>
                              {isFr ? "Actuelle" : "Current"}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] font-mono" style={{ color: "var(--pg-text-muted)" }}>
                          {isFr ? "Multiplicateur MO" : "Labor multiplier"} ×{c.factor.toFixed(2)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-base font-black tabular-nums" style={{ color: c.is_current ? "var(--pg-gold)" : "var(--pg-text-primary)" }}>
                          {fmt(c.grand_total)}
                        </div>
                        {!c.is_current && (
                          <div className="text-[10px] font-bold"
                            style={{ color: c.delta_vs_current > 0 ? "var(--pg-red)" : "#4ADE80" }}>
                            {c.delta_vs_current > 0 ? "+" : ""}{fmt(c.delta_vs_current)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <p className="text-[10px] mt-3" style={{ color: "var(--pg-text-muted)" }}>
                    {isFr
                      ? "Note : Les multiplicateurs régionaux s'appliquent uniquement sur la main-d'œuvre (CCQ). Les matériaux et taxes (TPS/TVQ) restent identiques."
                      : "Note: Regional multipliers apply to labor (CCQ) only. Materials and taxes (GST/QST) are identical."}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
